export const REDIRECT_STORAGE_KEY = 'intentico_redirect_after_login';
const FALLBACK = '/sales-home';
const BLOCKED_PATHS = ['/login', '/auth/callback', '/'];

/**
 * Persists the URL the user was trying to reach before being sent to /login.
 * Uses localStorage so the value survives both full-page SSO redirects and
 * the EmergencyReset routine (which clears sessionStorage entirely).
 * The key is listed in EmergencyReset's criticalItems so it is preserved
 * during version-bump resets.
 */
export function saveRedirectUrl(url: string): void {
  try {
    localStorage.setItem(REDIRECT_STORAGE_KEY, url);
  } catch {
    // Private browsing or storage full — silently ignore.
  }
}

/**
 * Returns the previously saved redirect URL without clearing it.
 * Safe to call multiple times (e.g. across React StrictMode double-mounts).
 */
export function getRedirectUrl(): string {
  try {
    const url = localStorage.getItem(REDIRECT_STORAGE_KEY);
    if (!url) return FALLBACK;

    // A stored return destination must remain a local application path.
    if (!url.startsWith('/') || url.startsWith('//') || /[\\\u0000-\u0020]/.test(url)) return FALLBACK;
    const path = new URL(url, 'https://intentico.invalid').pathname.toLowerCase();
    if (BLOCKED_PATHS.includes(path)) return FALLBACK;

    return url;
  } catch {
    return FALLBACK;
  }
}

/**
 * Returns and clears the previously saved redirect URL.
 * Use this for `window.location.href` assignments (SSO flows) where the
 * caller will not re-render.
 */
export function consumeRedirectUrl(): string {
  const url = getRedirectUrl();
  clearRedirectUrl();
  return url;
}

/**
 * Clears the stored redirect URL.
 */
export function clearRedirectUrl(): void {
  try {
    localStorage.removeItem(REDIRECT_STORAGE_KEY);
  } catch {
    // ignore
  }
}
